import { Component, Input, OnInit, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BigNumber } from 'bignumber.js';

import {
  Currency,
  CurrentEarnRate,
  CurrentMarketValue,
  EarnService,
  EarnTransfer,
  Ledger,
  MarketService,
  MarketValueService,
  Utils,
  LedgerType,
  SpinnerComponent,
  NoNumericSpinDirective,
  CurrencyAmountComponent,
} from 'core';

import { MessageService } from 'services/message/message.service';

@Component({
  selector: 'app-earn-transfer',
  templateUrl: 'earn-transfer.component.html',
  styleUrls: ['earn-transfer.component.scss'],
  imports: [SpinnerComponent, FormsModule, ReactiveFormsModule, NoNumericSpinDirective, CurrencyAmountComponent],
})
export class EarnTransferComponent implements OnInit {
  private readonly modalService = inject(BsModalService);
  private readonly marketService = inject(MarketService);
  private readonly marketValueService = inject(MarketValueService);
  private readonly earnService = inject(EarnService);
  private readonly messageService = inject(MessageService);

  @Input() tradeLedger: Ledger;
  @Input() earnLedger: Ledger;
  @Input() toEarn: boolean;
  @Input() agreementAccepted: boolean;
  @Input() agreementUrl: string;
  @Input() rate: CurrentEarnRate;
  @Input() ledgerType: LedgerType;
  @Input() callback: () => void;
  public usdCurrency: Currency;
  public zoomCurrency: Currency;
  public saving = false;
  public showAgreement = false;
  readonly LedgerType = LedgerType;

  private currentMarketValue: CurrentMarketValue;
  private zoomUsdMarketValue: CurrentMarketValue;

  transferFormGroup: UntypedFormGroup;

  ngOnInit(): void {
    this.transferFormGroup = new UntypedFormGroup(
      {
        amount: new UntypedFormControl('', [Validators.required, this.getMaxTransferValidator(), this.getPrecisionValidator()]),
        acceptAgreement: new UntypedFormControl(''),
        triggerAgreement: new UntypedFormControl(false),
      },
      [this.getMasterLoanAgreementAcceptedValidator()],
    );

    this.marketService.getCurrency(Currency.USD).then(
      (usdCurrency) => (this.usdCurrency = usdCurrency),
      (error) => console.log(error),
    );
    this.marketService.getCurrency(Currency.ZOOM).then(
      (zoomCurrency) => (this.zoomCurrency = zoomCurrency),
      (error) => console.log(error),
    );
    const ccy = this.currency.id;
    if (ccy != Currency.USD) {
      this.marketValueService.getCurrentMarketValueForSymbol(ccy + '/' + Currency.USD).then(
        (currentMarketValue) => (this.currentMarketValue = currentMarketValue),
        (error) => console.log(error),
      );
    }
    this.marketValueService.getCurrentMarketValueForSymbol(Currency.ZOOM + '/' + Currency.USD).then(
      (zoomUsdMarketValue) => (this.zoomUsdMarketValue = zoomUsdMarketValue),
      (error) => console.log(error),
    );
  }

  public hideModal(): void {
    this.modalService.hide();
  }

  public onTransfer(): void {
    if (!this.saving && this.transferFormGroup.valid) {
      if (this.toEarn && !this.agreementAccepted && !this.showAgreement) {
        this.showAgreement = true;
        this.transferFormGroup.controls.triggerAgreement.patchValue(true);
      } else {
        this.sendTransferToServer();
      }
    } else {
      this.transferFormGroup.markAllAsTouched();
    }
  }

  public moveAll(): void {
    if (this.maxPossibleTransfer) {
      this.transferFormGroup.patchValue({ amount: this.maxPossibleTransfer });
    }
  }

  public onlyNumericAllowed(e: KeyboardEvent): boolean {
    return Utils.onlyNumericAllowed(e, true);
  }

  public get formattedHoldHours(): string {
    return Utils.getFormattedLockPeriod(this.rate.stakingHoldHours);
  }

  private sendTransferToServer(): void {
    this.saving = true;
    this.earnService
      .transfer(
        new EarnTransfer(
          this.currency.id,
          this.ledgerType,
          this.transferFormGroup.value.amount,
          this.toEarn,
          this.agreementAccepted ? null : this.transferFormGroup.value.acceptAgreement === true,
        ),
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.messageService.success(this.successMessage);
            this.hideModal();
            this.callback();
          } else {
            this.messageService.error(response.errorMessage);
          }
          this.saving = false;
        },
        error: (error) => {
          this.messageService.showErrorMessage(error);
          this.saving = false;
        },
      });
  }

  private get successMessage(): string {
    const direction = this.toEarn ? 'moved to' : 'redeemed from';
    const walletType = this.rate.ledgerType === LedgerType.EARNING ? 'earn' : 'staking';

    return `Funds ${direction} your ${walletType} wallet`;
  }

  public get currency(): Currency {
    return this.tradeLedger ? this.tradeLedger.currency : this.earnLedger.currency;
  }

  public get placeholder(): string {
    return this.toEarn ? 'Amount to invest' : 'Amount to redeem';
  }

  public get usdEquivalent(): number | null {
    return this.currentMarketValue != null && this.currentMarketValue.price != null && this.transferFormGroup.value.amount
      ? this.currentMarketValue.price * this.transferFormGroup.value.amount
      : null;
  }

  public get interest(): number | null {
    return this.transferFormGroup.value.amount ? (this.transferFormGroup.value.amount * this.rate.apy) / 100 : null;
  }

  public get interestBonus(): number | null {
    const bonusInCcy = this.interest && this.rate.bonusRatePct ? (this.interest * this.rate.bonusRatePct) / 100 : null;

    return bonusInCcy !== null ? this.convertToZoom(bonusInCcy) : null;
  }

  public get interestUsd(): number | null {
    return this.currentMarketValue != null && this.currentMarketValue.price != null && this.interest
      ? this.currentMarketValue.price * this.interest
      : null;
  }

  private convertToZoom(bonusInCcy: number): number | null {
    if (this.currency.id === Currency.ZOOM) {
      return bonusInCcy;
    } else if (this.currency.id === Currency.USD && this.zoomUsdMarketValue !== null && this.zoomUsdMarketValue.price) {
      return bonusInCcy / this.zoomUsdMarketValue.price;
    } else if (
      this.currency.id !== Currency.USD
      && this.zoomUsdMarketValue !== null
      && this.zoomUsdMarketValue.price
      && this.currentMarketValue != null
      && this.currentMarketValue.price
    ) {
      return (bonusInCcy * this.currentMarketValue.price) / this.zoomUsdMarketValue.price;
    } else {
      return null;
    }
  }

  public get maxPossibleTransfer(): BigNumber {
    return this.toEarn ? this.tradeLedger?.availableBalance : this.earnLedger?.availableBalance;
  }

  private getMaxTransferValidator(): ValidatorFn {
    return (control) => {
      if (control.value !== null && !isNaN(control.value) && BigNumber(control.value).gt(this.maxPossibleTransfer)) {
        return { maxTransfer: true };
      }
      return null;
    };
  }

  private getPrecisionValidator(): ValidatorFn {
    return (control) => {
      if (
        control.value !== null
        && !isNaN(control.value)
        && this.currency
        && Utils.decimalPlaces(control.value) > this.currency.minorUnitsQty
      ) {
        return { precision: true };
      }
      return null;
    };
  }

  private getMasterLoanAgreementAcceptedValidator(): ValidatorFn {
    return (control) => {
      if (this.showAgreement && this.toEarn && !control.get('acceptAgreement')!.value) {
        return { agreementRequired: true };
      }
      return null;
    };
  }
}
